<template>
  <div class="wrapper-page">
    <el-card>
      <template #header>SSCMS 控制台</template>

      <el-tabs v-if="isSmsEnabled" v-model="form.type" :stretch="true" @tab-click="btnTypeClick">
        <el-tab-pane label="使用账号登录" name="account"></el-tab-pane>
        <el-tab-pane label="使用短信登录" name="mobile"></el-tab-pane>
      </el-tabs>

      <el-alert
        v-if="pageAlert.title"
        style="margin-bottom: 10px"
        :closable="false"
        :type="pageAlert.type"
        v-html="pageAlert.title"
      ></el-alert>

      <el-form v-show="form.type == 'account'" ref="formAccount" :model="form" label-width="0" status-icon>
        <el-form-item prop="account" :rules="[{ required: true, message: '请输入用户名/手机号/邮箱' }]">
          <el-input
            ref="account"
            v-model="form.account"
            placeholder="请输入用户名/手机号/邮箱"
            autocomplete="off"
            @keyup.enter="btnSubmitClick"
          >
            <template #prefix>
              <i class="el-input__icon el-icon-user-solid"></i>
            </template>
          </el-input>
        </el-form-item>
        <el-form-item prop="password" :rules="[{ required: true, message: '请输入密码' }]">
          <el-input
            v-model="form.password"
            type="password"
            name="password"
            placeholder="请输入密码"
            autocomplete="off"
            @keyup.enter="btnSubmitClick"
          >
            <template #prefix>
              <i class="el-input__icon el-icon-lock"></i>
            </template>
          </el-input>
        </el-form-item>

        <el-form-item prop="captchaValue" :rules="[{ required: true, message: '请输入验证码' }]">
          <el-input
            ref="captcha"
            v-model="form.captchaValue"
            placeholder="请输入验证码"
            autocomplete="off"
            @keyup.enter="btnSubmitClick"
          >
            <template #prefix>
              <i class="el-input__icon el-icon-picture"></i>
            </template>
            <template #append>
              <a href="javascript:;" @click="btnCaptchaClick">
                <img v-show="captchaUrl" style="display: none; height: 30px" :src="captchaUrl" />
              </a>
            </template>
          </el-input>
        </el-form-item>
      </el-form>
      <el-form v-show="form.type == 'mobile'" ref="formMobile" :model="form" label-width="0" status-icon>
        <el-form-item
          prop="mobile"
          :rules="[
            { required: true, message: '请输入手机号码' },
            { validator: validateMobile, message: '请输入有效的手机号码' },
          ]"
        >
          <el-input ref="mobile" placeholder="请输入手机号码" v-model="form.mobile" @keyup.enter="btnSubmitClick">
            <template #prefix>
              <i class="el-input__icon el-icon-mobile-phone"></i>
            </template>
          </el-input>
        </el-form-item>
        <el-form-item prop="code" :rules="[{ required: true, message: '请输入短信验证码' }]">
          <el-input v-model="form.code" autocomplete="off" placeholder="请输入短信验证码" @keyup.enter="btnSubmitClick">
            <template #prefix>
              <i class="el-input__icon el-icon-chat-line-square"></i>
            </template>
            <template #append>
              <el-link :underline="false" @click.prevent="btnSendSmsClick" style="padding: 0 15px">
                {{ countdown > 0 ? countdown + '秒' : '获取验证码' }}
              </el-link>
            </template>
          </el-input>
        </el-form-item>
      </el-form>

      <el-divider></el-divider>
      <div style="margin: 10px 0">
        <el-button type="primary" style="width: 100%" @click="btnSubmitClick">登 录</el-button>
      </div>
      <div style="margin: 10px 0">
        <el-button type="default" style="width: 100%" @click.prevent="btnRegisterClick">注 册</el-button>
      </div>
      <div style="margin: 10px 0; text-align: right;">
        <el-link
          style="margin-left: 10px"
          v-if="isSmsEnabled"
          @click.prevent="redirectLostPassword"
          :underline="false"
        >
          忘记密码？
        </el-link>
      </div>
    </el-card>
  </div>
</template>

<script lang="ts">
import utils from '@/utils';
import api from '@/api/login';
import { defineComponent } from 'vue';
import { IElForm } from '@/types/element';
import { auth } from '@/store';

interface IForm {
  type: string;
  account: string;
  password: string;
  mobile: string;
  code: string;
  captchaValue: string;
}

interface IState {
  status: number;
  redirect: string;
  pageAlert: {
    type: string;
    title: string;
  };
  captchaToken: string;
  captchaUrl: string;
  isSmsEnabled: boolean;
  countdown: number;
  form: IForm;
  validateMobile: (rule: { message: string }, value: string, callback: (error?: Error) => void) => void;
}

export default defineComponent({
  data() {
    return {
      status: utils.toInt(this.$route.query.status),
      redirect: this.$route.query.redirect,
      pageAlert: {},
      captchaToken: '',
      captchaUrl: '',
      isSmsEnabled: false,
      countdown: 0,
      form: {
        type: 'account',
        account: '',
        password: '',
        mobile: '',
        code: '',
        captchaValue: '',
      },
      validateMobile: utils.validateMobile,
    } as IState;
  },
  methods: {
    apiGet() {
      if (this.status === 401) {
        this.pageAlert = {
          type: 'error',
          title: '您的账号登录已过期或失效，请重新登录',
        };
      }

      api.get((res) => {
        this.isSmsEnabled = res.isSmsEnabled;
        this.apiCaptcha();
      });
    },

    apiCaptcha() {
      api.captchaNew((res) => {
        this.captchaToken = res.value;
        this.captchaUrl = api.getCaptchaUrl(this.captchaToken);
        this.btnTypeClick();
      });
    },

    apiCaptchaCheck() {
      api.captchaCheck(
        {
          token: this.captchaToken,
          value: this.form.captchaValue,
        },
        (res) => {
          if (!res.value) return;
          this.apiSubmit();
        },
        () => {
          api.captchaNew((res) => {
            this.captchaToken = res.value;
            this.captchaUrl = api.getCaptchaUrl(this.captchaToken);
            (this.$refs.captcha as HTMLInputElement).focus();
          });
        },
      );
    },

    apiSendSms() {
      api.sendSms(
        {
          mobile: this.form.mobile,
        },
        (res) => {
          if (!res.value) return;
          utils.notifySuccess('验证码发送成功，10分钟内有效');
          this.countdown = 60;
          const interval = setInterval(() => {
            this.countdown -= 1;
            if (this.countdown <= 0) {
              clearInterval(interval);
            }
          }, 1000);
        },
      );
    },

    apiSubmit() {
      api.submit(
        {
          isSmsLogin: this.form.type === 'mobile',
          account: this.form.account,
          password: this.form.password,
          mobile: this.form.mobile,
          code: this.form.code,
        },
        (res) => {
          auth.login(res.token, res.user);
          if (utils.isAbsoluteUrl(this.redirect)) {
            window.location.href = this.redirect;
          } else if (this.redirect) {
            utils.pageOpen(this.redirect);
          } else {
            this.redirectIndex();
          }
        },
        () => {
          this.apiCaptcha();
        },
      );
    },

    redirectIndex() {
      utils.pageOpen('/');
    },

    redirectLostPassword() {
      utils.pageOpen('lostPassword');
    },

    btnTypeClick() {
      if (this.$refs.formAccount) {
        (this.$refs.formAccount as IElForm).clearValidate();
      }
      if (this.$refs.formMobile) {
        (this.$refs.formMobile as IElForm).clearValidate();
      }
      if (this.form.type === 'account') {
        setTimeout(() => {
          if (this.$refs.account) {
            (this.$refs.account as HTMLInputElement).focus();
          }
        }, 100);
      } else if (this.form.type === 'mobile') {
        setTimeout(() => {
          if (this.$refs.mobile) {
            (this.$refs.mobile as HTMLInputElement).focus();
          }
        }, 100);
      }
    },

    btnCaptchaClick() {
      this.apiCaptcha();
    },

    isMobile(value: string) {
      return /^1[3-9][0-9]\d{8}$/.test(value);
    },

    btnSendSmsClick() {
      if (this.countdown > 0) return;
      if (!this.form.mobile) {
        utils.notifyError('手机号码不能为空');
        return;
      }
      if (!this.isMobile(this.form.mobile)) {
        utils.notifyError('请输入有效的手机号码');
        return;
      }

      this.apiSendSms();
    },

    btnSubmitClick() {
      if (this.form.type === 'account') {
        (this.$refs.formAccount as IElForm).validate((valid) => {
          if (valid) {
            this.apiCaptchaCheck();
          }
        });
      } else {
        (this.$refs.formMobile as IElForm).validate((valid) => {
          if (valid) {
            this.apiSubmit();
          }
        });
      }
    },

    btnRegisterClick() {
      if (this.redirect) {
        utils.pageOpen('register', { redirect: this.redirect });
      } else {
        utils.pageOpen('register');
      }
    },
  },
  mounted() {
    if (process.env.NODE_ENV === 'production') {
      const redirect = this.redirect || '/home/';
      window.location.href = `https://sscms.com/auth.html?redirect=${redirect}`;
    } else {
      this.apiGet();
    }
  },
});
</script>

<style scoped>
.el-tabs--top {
  margin-top: -15px;
}
.el-input-group__append {
  padding: 0;
}
.el-input-group__prepend {
  padding: 0 15px;
}
.el-icon-mobile-phone:before,
.el-icon-user-solid:before,
.el-icon-lock:before,
.el-icon-chat-line-square:before,
.el-icon-picture:before {
  width: 12px;
}
.wrapper-page {
  margin: 7.5% auto;
  max-width: 380px;
}
</style>
